import type { ComponentType, SVGProps } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus, FiTag } from "react-icons/fi";
import AdminSection from "../../../shared/components/AdminSection";
import Button from "../../../shared/components/Button";
import EmptyState from "../../../shared/components/EmptyState";
import EventActionButton from "../../../shared/components/EventActionButton";
import OrganizerDatePicker from "../../../shared/components/OrganizerDatePicker";
import Spinner from "../../../shared/components/Spinner";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  createDiscountCoupon,
  deleteDiscountCoupon,
  listDiscountCoupons,
  updateDiscountCoupon,
} from "../services/discountCouponsApi";
import {
  listEventChallengeLinks,
  type AdminEventChallengeLink,
} from "../services/eventRegistrationAdminService";
import { challengeTypeLabel } from "../../challenges/utils/challengeLabels";
import type {
  DiscountCoupon,
  DiscountCouponAppliesTo,
  DiscountCouponPayload,
  DiscountCouponStatus,
  DiscountCouponType,
} from "../types/discountCoupon";

type DiscountCouponDraft = {
  id?: number;
  code: string;
  description: string;
  discountType: DiscountCouponType;
  discountValue: string;
  appliesTo: DiscountCouponAppliesTo;
  tournamentId: string;
  eventId: string;
  challengeId: string;
  maxUses: string;
  validFrom: string;
  validUntil: string;
  isActive: boolean;
};

type Props = {
  token: string;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
};

type CouponTargetOption = {
  key: string;
  label: string;
  appliesTo: DiscountCouponAppliesTo;
  tournamentId: string;
  eventId: string;
  challengeId: string;
};

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;

const CouponIcon = FiTag as unknown as IconLike;
const PlusIcon = FiPlus as unknown as IconLike;

const emptyDraft: DiscountCouponDraft = {
  code: "",
  description: "",
  discountType: "percentage",
  discountValue: "",
  appliesTo: "general",
  tournamentId: "",
  eventId: "",
  challengeId: "",
  maxUses: "",
  validFrom: "",
  validUntil: "",
  isActive: true,
};

function formatDateInput(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function formatCurrencyFromCents(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Math.max(0, Number(value || 0)) / 100);
}

function formatDiscount(coupon: DiscountCoupon) {
  if (coupon.discountType === "percentage") return `${coupon.discountValue}%`;
  return formatCurrencyFromCents(coupon.discountValue);
}

function statusLabel(status: DiscountCouponStatus) {
  const labels: Record<DiscountCouponStatus, string> = {
    active: "Disponível",
    inactive: "Inativo",
    expired: "Expirado",
    exhausted: "Esgotado",
    scheduled: "Agendado",
  };
  return labels[status] || status;
}

function buildDraftFromCoupon(coupon: DiscountCoupon): DiscountCouponDraft {
  return {
    id: coupon.id,
    code: coupon.code,
    description: coupon.description || "",
    discountType: coupon.discountType,
    discountValue:
      coupon.discountType === "fixed_amount"
        ? String((Number(coupon.discountValue || 0) / 100).toFixed(2))
        : String(coupon.discountValue || ""),
    appliesTo: coupon.appliesTo,
    tournamentId: coupon.tournamentId ? String(coupon.tournamentId) : "",
    eventId: coupon.eventId || "",
    challengeId: coupon.challengeId ? String(coupon.challengeId) : "",
    maxUses: coupon.maxUses ? String(coupon.maxUses) : "",
    validFrom: formatDateInput(coupon.validFrom),
    validUntil: formatDateInput(coupon.validUntil),
    isActive: Boolean(coupon.isActive),
  };
}

function couponTargetKey(input: {
  appliesTo: DiscountCouponAppliesTo;
  eventId?: string | null;
  challengeId?: string | number | null;
  tournamentId?: string | number | null;
}) {
  if (input.appliesTo === "event" && String(input.eventId || "").trim()) {
    return `event:${String(input.eventId || "").trim()}`;
  }
  if (
    input.appliesTo === "challenge" &&
    Number.isFinite(Number(input.challengeId || 0)) &&
    Number(input.challengeId || 0) > 0
  ) {
    return `challenge:${Number(input.challengeId)}`;
  }
  if (
    input.appliesTo === "tournament" &&
    Number.isFinite(Number(input.tournamentId || 0)) &&
    Number(input.tournamentId || 0) > 0
  ) {
    return `tournament:${Number(input.tournamentId)}`;
  }
  return "general";
}

function targetOptionFromLink(
  link: AdminEventChallengeLink,
): CouponTargetOption | null {
  const tournamentId = Number(link.tournamentId || 0);
  if (!Number.isFinite(tournamentId) || tournamentId <= 0) return null;
  const type = String(link.challengeType || "").trim().toLowerCase();
  const appliesTo: DiscountCouponAppliesTo =
    type === "tournament" ? "tournament" : "challenge";
  const title = String(link.challengeName || "").trim() || `#${tournamentId}`;
  return {
    key: `${appliesTo}:${tournamentId}`,
    label: `${challengeTypeLabel(type === "tournament" ? "tournament" : "challenge")} · ${title}`,
    appliesTo,
    tournamentId: appliesTo === "tournament" ? String(tournamentId) : "",
    challengeId: appliesTo === "challenge" ? String(tournamentId) : "",
    eventId: "",
  };
}

function applyTargetToDraft(
  draft: DiscountCouponDraft,
  option?: CouponTargetOption,
): DiscountCouponDraft {
  if (!option) {
    return {
      ...draft,
      appliesTo: "general",
      tournamentId: "",
      challengeId: "",
      eventId: "",
    };
  }
  return {
    ...draft,
    appliesTo: option.appliesTo,
    tournamentId: option.tournamentId,
    challengeId: option.challengeId,
    eventId: option.eventId,
  };
}

function parseMoneyToCents(value: string) {
  const raw = String(value || "").trim();
  if (!raw) return 0;
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw.replace(/,/g, "");
  const amount = Number(normalized);
  if (!Number.isFinite(amount)) return 0;
  return Math.max(0, Math.round(amount * 100));
}

function buildPayload(draft: DiscountCouponDraft): DiscountCouponPayload {
  const discountValue =
    draft.discountType === "fixed_amount"
      ? parseMoneyToCents(draft.discountValue)
      : Math.round(Number(draft.discountValue || 0));
  const tournamentId = Math.floor(Number(draft.tournamentId || 0));
  const challengeId = Math.floor(Number(draft.challengeId || 0));
  const maxUses = Math.floor(Number(draft.maxUses || 0));
  return {
    code: draft.code,
    description: draft.description,
    discountType: draft.discountType,
    discountValue,
    appliesTo: draft.appliesTo,
    tournamentId:
      draft.appliesTo === "tournament" && tournamentId > 0
        ? tournamentId
        : null,
    challengeId:
      draft.appliesTo === "challenge" && challengeId > 0 ? challengeId : null,
    eventId:
      draft.appliesTo === "event" ? String(draft.eventId || "").trim() : null,
    maxUses: maxUses > 0 ? maxUses : null,
    validFrom: draft.validFrom || null,
    validUntil: draft.validUntil || null,
    isActive: draft.isActive,
  };
}

export default function AdminDiscountCoupons({
  token,
  onError,
  onSuccess,
}: Props) {
  const [coupons, setCoupons] = useState<DiscountCoupon[]>([]);
  const [targetOptions, setTargetOptions] = useState<CouponTargetOption[]>([]);
  const [draft, setDraft] = useState<DiscountCouponDraft>(emptyDraft);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  useBodyScrollLock(couponModalOpen);
  const [loading, setLoading] = useState(false);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const t = siteTexts.admin.discountCouponsTab;
  const editing = Number.isFinite(Number(draft.id));
  const selectedTargetKey = couponTargetKey(draft);

  const sortedCoupons = useMemo(
    () =>
      [...coupons].sort((a, b) =>
        String(a.code || "").localeCompare(String(b.code || ""), "pt-BR"),
      ),
    [coupons],
  );

  const targetOptionsByKey = useMemo(() => {
    const map = new Map<string, CouponTargetOption>();
    targetOptions.forEach((option) => {
      map.set(option.key, option);
    });
    return map;
  }, [targetOptions]);

  const loadCoupons = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setCoupons(await listDiscountCoupons(token));
    } catch (err) {
      onError(err instanceof Error ? err.message : t.toasts.loadError);
    } finally {
      setLoading(false);
    }
  }, [onError, t.toasts.loadError, token]);

  const loadTargets = useCallback(async () => {
    if (!token) return;
    setTargetsLoading(true);
    try {
      const links = await listEventChallengeLinks(token);
      const optionsByKey = new Map<string, CouponTargetOption>();
      links
        .map(targetOptionFromLink)
        .filter((option): option is CouponTargetOption => Boolean(option))
        .forEach((option) => {
          if (!optionsByKey.has(option.key)) optionsByKey.set(option.key, option);
        });
      const options = Array.from(optionsByKey.values()).sort((left, right) =>
        left.label.localeCompare(right.label, "pt-BR"),
      );
      setTargetOptions(options);
    } catch (err) {
      onError(err instanceof Error ? err.message : t.toasts.targetsLoadError);
    } finally {
      setTargetsLoading(false);
    }
  }, [onError, t.toasts.targetsLoadError, token]);

  useEffect(() => {
    void loadCoupons();
    void loadTargets();
  }, [loadCoupons, loadTargets]);

  const onSave = async () => {
    if (!token || saving) return;
    setSaving(true);
    try {
      const payload = buildPayload(draft);
      const saved = editing
        ? await updateDiscountCoupon(token, Number(draft.id), payload)
        : await createDiscountCoupon(token, payload);
      setCoupons((prev) => {
        const withoutCurrent = prev.filter((item) => item.id !== saved.id);
        return withoutCurrent.concat(saved);
      });
      setDraft(emptyDraft);
      setCouponModalOpen(false);
      onSuccess(editing ? t.toasts.updated : t.toasts.created);
    } catch (err) {
      onError(err instanceof Error ? err.message : t.toasts.saveError);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (coupon: DiscountCoupon) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await deleteDiscountCoupon(token, coupon.id);
      await loadCoupons();
      onSuccess(t.toasts.deleted);
    } catch (err) {
      onError(err instanceof Error ? err.message : t.toasts.deleteError);
    }
  };

  const onToggleActive = async (coupon: DiscountCoupon) => {
    try {
      const saved = await updateDiscountCoupon(token, coupon.id, {
        ...buildPayload(buildDraftFromCoupon(coupon)),
        isActive: !coupon.isActive,
      });
      setCoupons((prev) =>
        prev.map((item) => (item.id === saved.id ? saved : item)),
      );
      onSuccess(saved.isActive ? t.toasts.activated : t.toasts.deactivated);
    } catch (err) {
      onError(err instanceof Error ? err.message : t.toasts.saveError);
    }
  };

  const openCreateModal = () => {
    setDraft(emptyDraft);
    setCouponModalOpen(true);
  };

  const openEditModal = (coupon: DiscountCoupon) => {
    setDraft(buildDraftFromCoupon(coupon));
    setCouponModalOpen(true);
  };

  const closeCouponModal = () => {
    if (saving) return;
    setCouponModalOpen(false);
    setDraft(emptyDraft);
  };

  const couponForm = (
    <>
      <div className="admin-form-grid admin-discount-coupon-form">
        <label className="field">
          <span>{t.fields.code}</span>
          <input
            value={draft.code}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                code: event.target.value.toUpperCase(),
              }))
            }
            placeholder="TNP10"
          />
        </label>
        <label className="field">
          <span>{t.fields.description}</span>
          <input
            value={draft.description}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
          />
        </label>
        <label className="field">
          <span>{t.fields.type}</span>
          <select
            value={draft.discountType}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                discountType: event.target.value as DiscountCouponType,
                discountValue: "",
              }))
            }
          >
            <option value="percentage">{t.types.percentage}</option>
            <option value="fixed_amount">{t.types.fixedAmount}</option>
          </select>
        </label>
        <label className="field">
          <span>{t.fields.value}</span>
          <input
            value={draft.discountValue}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                discountValue: event.target.value,
              }))
            }
            placeholder={draft.discountType === "fixed_amount" ? "25,00" : "20"}
            inputMode="decimal"
          />
        </label>
        <label className="field">
          <span>{t.fields.appliesTo}</span>
          <select
            value={selectedTargetKey}
            onChange={(event) =>
              setDraft((prev) =>
                applyTargetToDraft(
                  prev,
                  targetOptionsByKey.get(event.target.value),
                ),
              )
            }
            disabled={targetsLoading}
          >
            <option value="general">{t.targets.all}</option>
            {targetOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
            {selectedTargetKey !== "general" &&
              !targetOptionsByKey.has(selectedTargetKey) && (
                <option value={selectedTargetKey}>
                  {t.targets.currentUnavailable}
                </option>
              )}
          </select>
        </label>
        <label className="field">
          <span>{t.fields.maxUses}</span>
          <input
            value={draft.maxUses}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, maxUses: event.target.value }))
            }
            placeholder={t.unlimited}
            inputMode="numeric"
          />
        </label>
        <OrganizerDatePicker
          label={t.fields.validFrom}
          value={draft.validFrom}
          disabledAfter={draft.validUntil || undefined}
          onChange={(value) =>
            setDraft((prev) => ({ ...prev, validFrom: value }))
          }
        />
        <OrganizerDatePicker
          label={t.fields.validUntil}
          value={draft.validUntil}
          disabledBefore={draft.validFrom || undefined}
          onChange={(value) =>
            setDraft((prev) => ({
              ...prev,
              validUntil: value,
            }))
          }
        />
        <label className="field">
          <span>{t.fields.status}</span>
          <select
            value={draft.isActive ? "active" : "inactive"}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                isActive: event.target.value === "active",
              }))
            }
          >
            <option value="active">{t.active}</option>
            <option value="inactive">{t.inactive}</option>
          </select>
        </label>
      </div>

      <div className="admin-modal-actions">
        <Button
          type="button"
          variant="pill"
          onClick={closeCouponModal}
          disabled={saving}
        >
          {siteTexts.common.cancel}
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={onSave}
          disabled={saving}
        >
          {saving ? "Salvando..." : editing ? t.saveEdit : t.create}
        </Button>
      </div>
    </>
  );

  return (
    <div className="admin-content">
      <AdminSection className="admin-users-dashboard">
        <div className="admin-users-hero">
          <div className="admin-users-hero-copy">
            <span className="admin-users-hero-icon" aria-hidden="true">
              <CouponIcon />
            </span>
            <div>
              <h2>{t.title}</h2>
              <p>Gerencie cupons de desconto para eventos, desafios e torneios.</p>
            </div>
          </div>
          <EventActionButton
            variant="primary"
            className="admin-history-create-button admin-tournaments-create-button"
            onClick={openCreateModal}
            disabled={saving}
            icon={<PlusIcon />}
            label={t.newCoupon}
          />
        </div>

        <div className="admin-discount-coupon-list-header">
          <h4>{t.listTitle}</h4>
          <Button
            type="button"
            variant="pill"
            onClick={loadCoupons}
            disabled={loading}
          >
            {t.refresh}
          </Button>
        </div>
        {loading ? (
          <p className="status-message">
            <Spinner label={t.loading} />
          </p>
        ) : sortedCoupons.length === 0 ? (
          <EmptyState
            className="admin-empty-state"
            icon={<span className="material-symbols-outlined">sell</span>}
            title={t.empty}
          />
        ) : (
          <div className="admin-list">
            {sortedCoupons.map((coupon) => {
              const targetLabel =
                targetOptionsByKey.get(couponTargetKey(coupon))?.label ||
                (coupon.appliesTo === "general"
                  ? t.targets.all
                  : t.appliesTo[coupon.appliesTo]);
              return (
                <div
                  key={coupon.id}
                  className="admin-list-item admin-discount-coupon-item"
                >
                  <div>
                    <div className="admin-discount-coupon-heading">
                      <strong>{coupon.code}</strong>
                      <span
                        className={`admin-discount-coupon-status status-${coupon.status}`}
                      >
                        {statusLabel(coupon.status)}
                      </span>
                    </div>
                    <p>
                      {formatDiscount(coupon)} ·{" "}
                      {coupon.maxUses
                        ? `${coupon.usedCount}/${coupon.maxUses} usos`
                        : `${coupon.usedCount} usos`}
                    </p>
                    <p>
                      {coupon.description || t.noDescription} · {targetLabel}
                    </p>
                  </div>
                  <div className="admin-actions-row">
                    <Button
                      type="button"
                      variant="pill"
                      onClick={() => openEditModal(coupon)}
                    >
                      {t.edit}
                    </Button>
                    <Button
                      type="button"
                      variant="pill"
                      onClick={() => onToggleActive(coupon)}
                    >
                      {coupon.isActive ? t.deactivate : t.activate}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => onDelete(coupon)}
                    >
                      {siteTexts.common.remove}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </AdminSection>

      {couponModalOpen && (
        <div
          className="admin-modal-backdrop admin-nested-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={editing ? t.saveEdit : t.create}
        >
          <div className="admin-modal admin-tournament-modal admin-discount-coupon-modal">
            <div className="admin-modal-header">
              <h3>{editing ? "Editar cupom" : t.newCoupon}</h3>
              <Button
                type="button"
                variant="pill"
                onClick={closeCouponModal}
                disabled={saving}
              >
                Fechar
              </Button>
            </div>
            {couponForm}
          </div>
        </div>
      )}
    </div>
  );
}
